import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Link,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  AppBar,
  Toolbar,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ThinLayout } from "../Layouts/ThinLayout";
import { AssignmentSubmissionStatus } from "../types";
import { Publish, GetApp, Fullscreen, Close } from "@mui/icons-material";
import { BlingCheckmark } from "../Components/BlingCheckmark";
import { FileAttachment } from "../Components/FileUpload/FileAttachment";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_ASSIGNMENT_SUBMISSION,
  UPDATE_ASSIGNMENT_SUBMISSION,
  GET_ASSIGNMENT_SUBMISSION_UPLOAD_URL,
  CONFIRM_ASSIGNMENT_SUBMISSION_UPLOAD,
  AssignmentSubmissionResult,
} from "../Queries";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { useTranslation } from "react-i18next";

export const UserAssignmentSubmission = () => {
  const [searchParams] = useSearchParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();
  const [submissionText, setSubmissionText] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Decompose query params
  const key = searchParams.get("key");
  const submissionKeyBlob = atob(key || "");
  const submissionParts = submissionKeyBlob.split(":");
  const submissionId = submissionParts[0];
  const submissionToken = submissionParts[1];

  //
  // Queries
  //

  const { loading: submissionLoading, data: submissionData } =
    useQuery<AssignmentSubmissionResult>(GET_ASSIGNMENT_SUBMISSION, {
      variables: { pk: submissionId },
      context: {
        headers: {
          Authorization: `JWT ${submissionToken}`,
        },
      },
      onCompleted: (data) => {
        // Set initial submission text from existing data
        if (data.assignmentSubmission.submissionText) {
          setSubmissionText(data.assignmentSubmission.submissionText);
        }
      },
      onError: (error) =>
        setError("Error loading assignment submission", error),
    });

  //
  // Mutations
  //

  const [updateSubmission] = useMutation(UPDATE_ASSIGNMENT_SUBMISSION, {
    onError: (error) => setError("Error updating assignment submission", error),
  });

  const [getUploadUrl] = useMutation(GET_ASSIGNMENT_SUBMISSION_UPLOAD_URL, {
    context: {
      headers: {
        Authorization: `JWT ${submissionToken}`,
      },
    },
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message || "Failed to get upload URL");
    },
  });

  const [confirmUpload] = useMutation(CONFIRM_ASSIGNMENT_SUBMISSION_UPLOAD, {
    context: {
      headers: {
        Authorization: `JWT ${submissionToken}`,
      },
    },
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message || "Failed to confirm upload");
    },
    onCompleted: () => {
      setFileUploading(false);
      setFileUploadError(null);
    },
  });

  const submission = submissionData?.assignmentSubmission;
  const assignment = submission?.assignment;
  const showLoading = submissionLoading;

  if (showLoading) {
    return (
      <ThinLayout title="Loading..." loading={true}>
        <div />
      </ThinLayout>
    );
  }

  if (!submission || !assignment) {
    return (
      <ThinLayout title="Error">
        <Typography variant="h4">Assignment submission not found</Typography>
      </ThinLayout>
    );
  }

  if (submission.status === AssignmentSubmissionStatus.Submitted) {
    return (
      <ThinLayout title="">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            opacity: 1,
            animation: "fade 2s linear",
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 600 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              You&apos;re all done! Your assignment submission has been
              received.
            </Typography>
            <BlingCheckmark
              active={false}
              completed={true}
              size="large"
              divProps={{ style: { marginTop: 20 } }}
            />

            {submission.submissionText && (
              <Card sx={{ mt: 4, textAlign: "left" }}>
                <CardHeader title="Your Submission" />
                <CardContent>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {submission.submissionText}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Show original submitted file if it exists */}
            {submission.submissionFileUrl && (
              <Card sx={{ mt: 2, textAlign: "left" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Submitted File:
                  </Typography>
                  <Link
                    href={submission.submissionFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <GetApp sx={{ mr: 1 }} />
                    Download File
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Show attached file in success screen */}
            {attachedFile && (
              <Card sx={{ mt: 2, textAlign: "left" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Attached File:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <GetApp sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {attachedFile.name} (
                      {(attachedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </ThinLayout>
    );
  }

  // Event Handlers
  async function handleSubmissionChange(updates: any) {
    if (!submission) return;

    await updateSubmission({
      variables: {
        id: submission.id,
        ...updates,
      },
      context: {
        debounceKey: `assignment-submission-${submission.id}`,
        headers: {
          Authorization: `JWT ${submissionToken}`,
        },
      },
      optimisticResponse: {
        updateAssignmentSubmission: {
          __typename: "AssignmentSubmission",
          ...submission,
          ...updates,
        },
      },
    });
  }

  function handleSubmit() {
    if (!submission || (!submissionText.trim() && !attachedFile)) {
      return;
    }

    handleSubmissionChange({
      status: AssignmentSubmissionStatus.Submitted,
      submissionText: submissionText,
      submittedDate: new Date().toISOString(),
    });
  }

  function handleSaveDraft() {
    if (!submission) {
      return;
    }

    handleSubmissionChange({
      submissionText: submissionText,
    });
  }

  // Get upload URL handler
  const handleGetUploadUrl = async (filename: string, fileSize: number) => {
    if (!submission) throw new Error("No submission found");

    setFileUploading(true);
    setFileUploadError(null);

    try {
      const result = await getUploadUrl({
        variables: {
          submissionId: submission.id,
          filename: filename,
          fileSize: fileSize,
        },
      });

      return result.data?.getAssignmentSubmissionUploadUrl;
    } catch (error) {
      setFileUploading(false);
      throw error;
    }
  };

  // Confirm upload handler
  const handleConfirmUpload = async (
    fileUrl: string,
    originalFilename: string,
  ) => {
    if (!submission) throw new Error("No submission found");

    try {
      await confirmUpload({
        variables: {
          submissionId: submission.id,
          fileUrl: fileUrl,
          originalFilename: originalFilename,
        },
      });
      // Success state is handled by the mutation's onCompleted callback
    } catch (error) {
      setFileUploading(false);
      throw error;
    }
  };

  const canSubmit =
    submissionText.trim().length > 0 ||
    attachedFile !== null ||
    submission?.submissionFileUrl;
  const isOverdue = Boolean(
    assignment.assignmentDueDate &&
      new Date() > new Date(assignment.assignmentDueDate),
  );
  const isClosed = Boolean(
    assignment.assignmentCloseDate &&
      new Date() > new Date(assignment.assignmentCloseDate),
  );

  return (
    <ThinLayout title={"Assignment Submission"} fullWidth={true}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column: Assignment Details & Preview */}
          <Grid item xs={12} md={6} xl={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                {assignment.description && (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      Instructions:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mb: 2, whiteSpace: "pre-wrap" }}
                    >
                      {assignment.description}
                    </Typography>
                  </>
                )}

                {assignment.pdfAttachmentUrl && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        Attached File:
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Tooltip title="View Full Screen">
                          <IconButton
                            aria-label="Open full-screen preview"
                            size="small"
                            onClick={() => setIsPreviewOpen(true)}
                          >
                            <Fullscreen />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Assignment File">
                          <IconButton
                            component="a"
                            href={assignment.pdfAttachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download assignment file"
                            size="small"
                          >
                            <GetApp />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        height: 600,
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "#fafafa",
                      }}
                    >
                      <iframe
                        src={assignment.pdfAttachmentUrl}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                        title="Assignment PDF Preview"
                      />
                    </Box>
                    <Dialog
                      fullScreen
                      open={isPreviewOpen}
                      onClose={() => setIsPreviewOpen(false)}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <AppBar sx={{ position: "relative" }}>
                          <Toolbar>
                            <IconButton
                              edge="start"
                              color="inherit"
                              onClick={() => setIsPreviewOpen(false)}
                              aria-label={t(
                                "modals.userAssignmentSubmission.closeAria",
                              )}
                            >
                              <Close />
                            </IconButton>
                            <Typography
                              sx={{ ml: 2, flex: 1 }}
                              variant="h6"
                              component="div"
                            >
                              {t(
                                "modals.userAssignmentSubmission.attachmentTitle",
                              )}
                            </Typography>
                            <IconButton
                              color="inherit"
                              component="a"
                              href={assignment.pdfAttachmentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={t(
                                "modals.userAssignmentSubmission.downloadAria",
                              )}
                            >
                              <GetApp />
                            </IconButton>
                          </Toolbar>
                        </AppBar>
                        <Box sx={{ flex: 1, bgcolor: "#000" }}>
                          <iframe
                            src={assignment.pdfAttachmentUrl}
                            width="100%"
                            height="100%"
                            style={{ border: "none" }}
                            title="Assignment PDF Fullscreen Preview"
                          />
                        </Box>
                      </Box>
                    </Dialog>
                  </Box>
                )}

                {assignment.assignmentDueDate && (
                  <Typography
                    variant="body2"
                    color={isOverdue ? "error" : "text.secondary"}
                  >
                    Due:{" "}
                    {new Date(
                      assignment.assignmentDueDate,
                    ).toLocaleDateString()}{" "}
                    at{" "}
                    {new Date(
                      assignment.assignmentDueDate,
                    ).toLocaleTimeString()}
                    {isOverdue && " (OVERDUE)"}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Submission Form */}
          <Grid item xs={12} md={6} xl={6}>
            <Card>
              <CardHeader title="Your Submission" />
              <CardContent>
                <TextField
                  label="Assignment Submission"
                  placeholder="Enter your assignment submission here..."
                  multiline
                  rows={8}
                  fullWidth
                  value={submissionText}
                  onChange={(e) => setSubmissionText(e.target.value)}
                  disabled={isClosed}
                  sx={{ mb: 3 }}
                />

                {/* File Attachment Component */}
                <FileAttachment
                  onFileChange={setAttachedFile}
                  onGetUploadUrl={handleGetUploadUrl}
                  onConfirmUpload={handleConfirmUpload}
                  onUploadError={(message) => {
                    setFileUploading(false);
                    setFileUploadError(message);
                  }}
                  disabled={isClosed}
                  acceptedFileTypes=".pdf,.doc,.docx,.txt,.ppt,.pptx"
                  maxFileSize={10}
                  uploading={fileUploading}
                  uploadError={fileUploadError}
                  existingFileUrl={submission?.submissionFileUrl}
                  existingFileName={
                    submission?.submissionFileUrl
                      ? submission.submissionFileUrl.split("/").pop()
                      : undefined
                  }
                  autoUpload={true}
                />

                <Box sx={{ mt: 3 }}>
                  {isClosed ? (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                      This assignment is closed and no longer accepting
                      submissions.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        onClick={handleSaveDraft}
                        disabled={!submissionText.trim() && !attachedFile}
                      >
                        Save Draft
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        startIcon={<Publish />}
                      >
                        Submit Assignment
                      </Button>
                    </Box>
                  )}
                </Box>

                {isOverdue && !isClosed && (
                  <Typography
                    color="warning.main"
                    variant="body2"
                    sx={{ mt: 2 }}
                  >
                    This assignment is past due, but you can still submit.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ThinLayout>
  );
};
